<template>
    <CieloLayout :show-merchant-name="productHasShipping" :starting-point-by-slot="productHasShipping">
        <div class="change-payment-type">
            <h4 class="change-payment-type__title" v-text="$ml.get('whatPayment')" tabindex="0" />

            <div class="change-payment-type__container--express" v-if="isGooglePayEnabledMerchant || isApplePayEnabledMerchant">
                <ExpressPayment
                    :is-google-pay-enabled="isGooglePayEnabled"
                    :on-g-p-cancel="onGPCancel"
                    :on-g-p-click="onGPClick"
                    :on-g-p-load-payment-data="onGPLoadPaymentData"
                    :is-apple-pay-enabled="isApplePayEnabled"
                    @applePayClick="handleApplePayButtonClick"
                />
            </div>

            <div class="change-payment-type__container--dof" v-if="this.hasDofData">
                <CardSection @cardClick="handleCardClick" />
            </div>

            <div class="change-payment-type__container">
                <ul class="payment-type__list" tabindex="0" :aria-label="$ml.get('ariaPaymentTypeList')">
                    <li
                        class="payment-type__list-item"
                        v-for="(walletType, index) in filteredWalletTypes"
                        :key="index"
                    >
                        <button
                            class="payment-type"
                            @click="handleDefaultWalletType(walletType)"
                        >
                            <img class="payment-type__img" :src="walletType.Icon" alt="" tabindex="-1" />

                            <p class="payment-type__info">
                                <span
                                    class="payment-type__name"
                                    v-text="$ml.get(walletType.Title)"
                                />
                                <small class="payment-type__text" v-if="walletType.Text">
                                    <span v-text="$ml.get(walletType.Text)" />
                                </small>
                            </p>

                            <span class="flag flag--new" v-text="$ml.get('new')" v-if="walletType.IsNew" />

                            <img class="payment-type__chevron" src="@/assets/img/icons/arrow-down-blue.svg" alt="" tabindex="-1" />
                        </button>
                    </li>
                </ul>

                <ul class="payment-type__list" tabindex="0" :aria-label="$ml.get('ariaPaymentTypeList')">
                    <li
                        class="payment-type__list-item"
                        v-for="(paymentType, index) in availablePaymentTypes"
                        :key="index"
                    >
                        <button
                            class="payment-type"
                            @click="handePaymentType(paymentType)"
                        >
                            <img class="payment-type__img" :src="paymentType.Icon" alt="" tabindex="-1" />

                            <p class="payment-type__info">
                                <span
                                    class="payment-type__name"
                                    v-text="$ml.get(
                                        getPaymentMethodTranslationParameterName(
                                            paymentType.Id
                                        )
                                    )
                                    "
                                />
                                <small v-if="paymentType.discount" class="payment-type__discount-info">
                                    {{ paymentType.discount }}%
                                    <span v-text="$ml.get('off')" />
                                </small>
                                <small v-else-if="paymentType.Text" class="payment-type__text">
                                    <span v-text="$ml.get(paymentType.Text)" />
                                </small>
                            </p>

                            <img class="payment-type__chevron" src="@/assets/img/icons/arrow-down-blue.svg" alt="" tabindex="-1" />
                        </button>
                    </li>
                </ul>
            </div>
        </div>

        <CieloButtonV2 v-if="productHasShipping" @click="goToPreviousPage" :transparent="true">
            <span :style="{ 'font-weight': 600 }" v-text="$ml.get('back')" />
        </CieloButtonV2>
    </CieloLayout>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import ProductHelper from '@/helpers/productHelper';
import loader from '@/helpers/loader';

const CieloLayout = () =>
    import(/* webpackChunkName: "cielo-layout" */ '@/components/layout/CieloLayout');
const CieloButtonV2 = () =>
    import(/* webpackChunkName: "cielo-button" */ '@/components/template/CieloButtonV2');

import { logEvent } from '@/helpers/cieloLogger';
import vars from '@/config/vars';
import ExpressPayment from '@/components/ClickToPay/ExpressPayment';
import CardSection from '@/components/CardSection';
import translationParameterHelper from '@/config/lang/helpers/translationParameterHelper.js';
import PaymentTypes from '@/models/PaymentTypes';
import { shouldRenderShippingView } from '@/helpers/viewRules';

export default {
    components: {
        CieloLayout,
        ExpressPayment,
        CieloButtonV2,
        CardSection,
    },
    created() {
        loader.multiLanguage = this.$ml;
    },
    mounted() {
        this.$store.commit('payment/resetPaymentMethod');

        if(this.isGooglePayEnabledMerchant) {
            this.$store.commit('googlePayPayment/setTotalPrice', this.totalPrice.toString());
        }

        if(this.isApplePayEnabledMerchant) {
            this.$store.commit('applePayPayment/setTotalPrice', this.totalPrice.toString());
        }

        if(this.productHasShipping) {
            this.$store.commit('shippingType/setShowShippingMethod', true);
        }
    },

    computed: {
        ...mapGetters({
            availablePaymentTypes: 'paymentTypes/getFilteredAvailable',
            totalPrice: 'payment/getTotalPrice',
            availablePixTypes: 'paymentTypes/getPixTypes',
            hasDofData: 'dataOnFile/hasDofData',
        }),
        ...mapState({
            preorderId: state => state.preorder.Id,
            isGooglePayEnabledMerchant: state => state.googlePayPayment.isGooglePayEnabled,
            isGooglePayReadyToPay: state => state.googlePayPayment.isReadyToPay,
            isApplePayEnabledMerchant: state => state.applePayPayment.isApplePayEnabled,
            isApplePayReadyToPay: state => state.applePayPayment.isReadyToPay,
        }),
        productHasShipping: () => ProductHelper.productHasShipping(),
        showDetailedPrice() {
            return shouldRenderShippingView() == false;
        },
        isGooglePayEnabled() {
            if (this.isGooglePayEnabledMerchant && this.isGooglePayReadyToPay) {
                return true;
            }
            return false;
        },

        isApplePayEnabled() {
            if (this.isApplePayEnabledMerchant && this.isApplePayReadyToPay) {
                return true;
            }
            return false;
        },

        filteredWalletTypes() {
            return this.availableWalletTypes.filter(type => type.Available);
        },

        availableWalletTypes() {
            return this.$store.getters['walletTypes/getAvailable'];
        },
    },

    data: () => {
        return {
            showAppDownloadButtons: false,
        };
    },

    methods: {
        /**
         * Faz o handle do tipo de pagamento.
         * Caso disponível: Altera o meio de pagamento.
         * Caso indisponível: Redireciona para o checkout legado.
         * @param {object} paymentType Meio de pagamento a ser manipulado.
         */
        handePaymentType(paymentType) {
            if (paymentType.Available) {
                this.handleAvailablePaymentType(paymentType);
            } else {
              this.handleUnavailablePaymentType(paymentType);
            }
        },
        /**
         * Caso não esteja disponível, loga que não está disponível e redireciona para o checkout legado.
         * @param {object} paymentType Meio de pagamento a ser manipulado.
         */
        handleUnavailablePaymentType(paymentType) {
            logEvent({
                category: 'checkout',
                action: 'selecao_pagamento',
                label: 'Redirecionando para o checkout legado'
            });

            window.location.href = `${vars.CIELO_CHECKOUT_URL
                }/TransactionalVNext/Checkout/Finalize/${this.preorderId}?type=0`;
        },

        goToPreviousPage() {
            this.$store.dispatch('pipelineV2/pushPrevious');
        },

        /**
         * Caso o tipo de pagamento esteja disponível, altera o tipo e segue
         * para a página de start do meio de pagamento
         * @param {object} paymentType Meio de pagamento a ser manipulado.
         */
        handleAvailablePaymentType(paymentType) {
            if(this.isPixType(paymentType.Id) && this.availablePixTypes.length > 1) {
                this.$store.dispatch('pipelineV2/pushTo', 'ChangePixType');
            } else {
                logEvent({
                    category: 'checkout',
                    action: 'selecao_pagamento',
                    label: `PaymentType: ${paymentType.Id}`
                });
                this.$store.commit('payment/setPaymentType', paymentType.Id);
                this.$store.commit('payment/setDiscount', paymentType.discount);

                if (paymentType.paymentMethodId) {
                    this.$store.dispatch(
                        'payment/setPaymentMethod',
                        paymentType.paymentMethodId,
                        { root: true }
                    );
                }

                this.$store.dispatch('pipelineV2/pushTo', paymentType.StartView);
            }
        },

        handleDefaultWalletType(walletType) {
            logEvent({
                category: walletType.LogCategory,
                action: walletType.LogAction,
                label: walletType.LogLabel
            });

            this.$store.commit('payment/setPaymentType', walletType.Id);
            this.$store.dispatch('pipelineV2/pushTo', walletType.StartView);
        },

        startLoader() {
            loader.Start(`${this.$ml.get('wait')}...`);
        },

        getPaymentMethodTranslationParameterName(type) {
            return translationParameterHelper.getPaymentMethodTranslationParameterName(
                type
            );
        },

        goToGPInstallments() {
            this.$store.commit(
                'payment/setPaymentType',
                PaymentTypes.GOOGLE_PAY
            );

            this.$store.dispatch('pipelineV2/pushTo', 'GooglePayCardInstallments');
        },

        goToAPInstallments() {
            this.$store.commit(
                'payment/setPaymentType',
                PaymentTypes.APPLE_PAY
            );

            this.$store.dispatch('pipelineV2/pushTo', 'ApplePayCardInstallments');
        },

        //GP (Google Pay) callbacks methods
        onGPCancel() {
            logEvent({
                category: 'google_pay',
                action: 'fechar_popup_gpay',
                label: 'Usuário fecha popup do Google pay.'
            });
        },

        onGPClick() {
            logEvent({
                category: 'google_pay',
                action: 'forma_pagamento_gpay',
                label: 'Usuário escolhe a forma de pagamento.'
            });
        },

        onGPLoadPaymentData(data) {
            logEvent({
                category: 'google_pay',
                action: 'escolher_cartao_gpay',
                label: 'Usuario acessa o popup do gpay na tela pra escolher ou cadastrar o cartão.'
            });

            this.goToGPInstallments();
        },

        //Apple Pay
        handleApplePayButtonClick() {
            logEvent({
                category: 'apple_pay',
                action: 'forma_pagamento_apay',
                label: 'Usuário escolhe a forma de pagamento expressa - clicando no batao do Apple Pay.'
            });

            this.goToAPInstallments();
        },

        //DOF
        handleCardClick(card) {
            this.$store.dispatch('dataOnFile/setSelectedCardId', card);
            this.$store.commit(
                'payment/setPaymentType',
                PaymentTypes.DOF
            );
            this.$store.dispatch('pipelineV2/pushTo', 'DataOnFileReview');
        },
        
        isPixType(id) {
            return id === PaymentTypes.PIX || id === PaymentTypes.PIX_OPEN_FINANCE;
        }
	  }
};
</script>

<style lang="scss" scoped>
    .change-payment-type {
        margin-bottom: 36px;

        @media screen and (max-width: 1024px) {
            display: flex;
            flex-direction: column;
            flex: 1 0 auto;
        }

        @media screen and (min-width: 1025px) {
            margin-bottom: 32px;
        }


        &__container {
            margin-bottom: 12px;

            @media screen and (max-width: 1024px) {
                flex: 1 0 auto;
            }

            @media screen and (min-width: 1025px) {
                margin-bottom: 32px;
            }
        }

        &__container--express {
            margin-bottom: 13px;
        }

        &__container--dof {}

        &__title {
            font-size: 16px;
            font-weight: 600;
            line-height: 16px;
            color: #2F363E;
            margin-bottom: 20px;

            @media screen and (min-width: 1025px) {
                margin-bottom: 32px;
            }
        }

        &__subtitle {
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            color: #2F363E;
            margin-bottom: 20px;
        }

    }

    .payment-type {
        display: flex;
        align-items: center;
        position: relative;
        padding: 16px 52px 16px 16px;
        background-color: transparent;
        border: none;
        width: 100%;
        border-radius: 12px;

        &:hover,
        &:focus {
            background-color: #EFF1F3;
        }

        &__list {
          list-style-type: none;
          padding: 0;
        }

        &__list-item {
            border-radius: 12px;
            border: 1.5px solid #b8C1CC;
            background-color: #FFF;
            &:not(:last-of-type) {
                margin-bottom: 20px;
            }
        }

        &__img {
            width: 32px;
            height: 32px;
            margin-right: 12px;
        }

        &__info {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            margin-bottom: 0;
        }

        &__name {
            font-size: 16px;
            font-weight: 600;
            line-height: 10px;
            color: #2F363E;
        }

        &__discount-info {
            font-size: 14px;
            font-weight: 600;
            line-height: 10px;
            color: #00855D;
            margin-top: 12px;
        }

        &__text {
            font-size: 14px;
            font-weight: 500;
            line-height: 10px;
            color: #5C6A7B;
            margin-top: 12px;
        }

        &__chevron {
            position: absolute;
            top: 50%;
            right: 16px;
            transform: translateY(-50%) rotate(-90deg);
            width: 24px;
            height: 24px;
        }

        .flag {
            position: absolute;
            top: 50%;
            right: 52px;
            transform: translateY(-50%);
            background-color: #9747FF29;
            color: #5B169C;
            padding: 6px 8px;
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            border-radius: 8px;
        }

    }
</style>
